"use client";

import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { Box, Heading, Text, Flex, Badge, Tabs } from "@radix-ui/themes";
import Image from "next/image";
import { listInvestmentPlans } from "@/actions/investments/list";
import { RotateSpinnerComponent } from "@/components/RotateSpinner";

interface InvestmentPlansContainerProps {
  showHeader?: boolean;
  showButtons?: boolean;
  className?: string;
}

export function InvestmentPlansContainer({
  showHeader = true,
  showButtons = false,
  className = "",
}: InvestmentPlansContainerProps) {
  const [plans, setPlans] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<string>("0");
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await listInvestmentPlans();
        setPlans(res as any);
        if (res.length > 0) setActiveTab("0");
      } catch (error) {
        console.error("Error loading investment plans:", error);
      } finally {
        setIsLoadingPlans(false);
      }
    })();
  }, []);

  if (isLoadingPlans) {
    return <RotateSpinnerComponent />;
  }

  const planColors = [
    {
      color: "text-green-600",
      badge: "green",
      cardStyle: "hover:shadow-green-500/20",
      bg: "bg-green-500/10",
      border: "border-green-500/50",
      tabBg: "data-[state=active]:bg-green-500/10",
    },
    {
      color: "text-blue-600",
      badge: "blue",
      cardStyle: "hover:shadow-blue-500/20",
      bg: "bg-blue-500/10",
      border: "border-blue-500/50",
      tabBg: "data-[state=active]:bg-blue-500/10",
    },
    {
      color: "text-purple-600",
      badge: "purple",
      cardStyle: "hover:shadow-purple-500/20",
      bg: "bg-purple-500/10",
      border: "border-purple-500/50",
      tabBg: "data-[state=active]:bg-purple-500/10",
    },
    {
      color: "text-orange-600",
      badge: "orange",
      cardStyle: "hover:shadow-orange-500/20",
      bg: "bg-orange-500/10",
      border: "border-orange-500/50",
      tabBg: "data-[state=active]:bg-orange-500/10",
    },
  ];

  if (plans.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="text-center py-12"
      >
        <Text size="3" color="gray">
          No investment plans available at the moment.
        </Text>
      </motion.div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {showHeader && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <Heading size="8" className="mb-3">
            Investment Plans
          </Heading>
          <Text size="3" color="gray" className="max-w-3xl mx-auto">
            Choose the best investment plan for your financial goals. Each plan
            offers multiple tiers with unique returns based on your investment
            amount and duration.
          </Text>
        </motion.div>
      )}

      <Tabs.Root value={activeTab} onValueChange={setActiveTab}>
        {/* Tab List */}
        <Tabs.List size="2" className="mb-8 flex-wrap justify-center">
          {plans.map((plan, idx) => {
            const style = planColors[idx % planColors.length];
            return (
              <Tabs.Trigger
                key={idx}
                value={String(idx)}
                className={`cursor-pointer transition-all ${style.tabBg}`}
              >
                <Flex align="center" gap="2">
                  {/* Plan Icon */}
                  {plan.icon && (
                    <div className="w-6 h-6 relative">
                      <Image
                        src={plan.icon}
                        alt={plan.name}
                        fill
                        className="object-contain"
                      />
                    </div>
                  )}
                  <Text weight="medium">{plan.name}</Text>
                  <Badge color={style.badge as any} size="1" variant="soft">
                    {plan.tiers?.length || 0}
                  </Badge>
                </Flex>
              </Tabs.Trigger>
            );
          })}
        </Tabs.List>

        {/* Tab Content */}
        {plans.map((plan, planIdx) => {
          const style = planColors[planIdx % planColors.length];

          return (
            <Tabs.Content key={planIdx} value={String(planIdx)}>
              <AnimatePresence mode="wait">
                <motion.div
                  key={planIdx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {/* Tiers Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3  gap-5 ">
                    {plan.tiers?.map((tier: any, tierIdx: number) => {
                      const minAnchor = tier.anchors?.[0];
                      const maxAnchor = tier.anchors?.[tier.anchors.length - 1];

                      return (
                        <motion.div
                          key={tierIdx}
                          initial={{ scale: 0.9, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{
                            duration: 0.3,
                            delay: tierIdx * 0.05,
                          }}
                        >
                          <Box
                            className={`bg-[--gray-a2] border border-[--gray-a6] rounded-xl p-5 transition-all duration-300 hover:border-[--gray-a8] ${style.cardStyle} h-full`}
                          >
                            {/* Tier Badge */}
                            <Flex justify="center" className="mb-4">
                              <Badge
                                color={style.badge as any}
                                size="2"
                                variant="surface"
                              >
                                {tier.name || `Tier ${tierIdx + 1}`}
                              </Badge>
                            </Flex>

                            {/* Tier Description */}
                            {tier.description && (
                              <Text
                                size="1"
                                color="gray"
                                className="text-center mb-4 block"
                              >
                                {tier.description}
                              </Text>
                            )}

                            {/* Investment Range */}
                            <div className="text-center mb-5">
                              <Text size="1" color="gray" className="mb-2">
                                Investment Range
                              </Text>
                              <Heading
                                size="5"
                                className={`${style.color} mb-1`}
                              >
                                ${tier.minAmount.toLocaleString()}
                              </Heading>
                              <Text size="1" color="gray" className="mb-1">
                                to
                              </Text>
                              <Heading size="5" className={style.color}>
                                ${tier.maxAmount.toLocaleString()}
                              </Heading>
                            </div>

                            <div className="border-t border-[--gray-a5] my-4"></div>

                            {/* Profit Range */}
                            {minAnchor && maxAnchor && (
                              <div className="space-y-3">
                                <Text
                                  size="1"
                                  weight="bold"
                                  className={`${style.color} block text-center uppercase tracking-wide`}
                                >
                                  Profit Range
                                </Text>
                                <div className="space-y-2">
                                  <Flex
                                    justify="between"
                                    align="center"
                                    className={`${style.bg} rounded-lg p-2.5 border ${style.border}`}
                                  >
                                    <Text size="1" weight="medium">
                                      {minAnchor.days} days
                                    </Text>
                                    <Text
                                      size="2"
                                      weight="bold"
                                      className={style.color}
                                    >
                                      {parseFloat(
                                        minAnchor.profitAmount
                                      ).toFixed(0)}
                                      %
                                    </Text>
                                  </Flex>
                                  {maxAnchor.days !== minAnchor.days && (
                                    <Flex
                                      justify="between"
                                      align="center"
                                      className={`${style.bg} rounded-lg p-2.5 border ${style.border}`}
                                    >
                                      <Text size="1" weight="medium">
                                        {maxAnchor.days} days
                                      </Text>
                                      <Text
                                        size="2"
                                        weight="bold"
                                        className={style.color}
                                      >
                                        {parseFloat(
                                          maxAnchor.profitAmount
                                        ).toFixed(2)}
                                        %
                                      </Text>
                                    </Flex>
                                  )}
                                </div>

                                {/* Duration Options */}
                                {tier.anchors && tier.anchors.length > 0 && (
                                  <div className="pt-3">
                                    <Text
                                      size="1"
                                      color="gray"
                                      className="mb-2 block text-center"
                                    >
                                      Available Durations
                                    </Text>
                                    <Flex gap="1" wrap="wrap" justify="center">
                                      {tier.anchors.map(
                                        (anchor: any, idx: number) => (
                                          <Badge
                                            key={idx}
                                            color={style.badge as any}
                                            size="1"
                                            variant="soft"
                                          >
                                            {anchor.days}d
                                          </Badge>
                                        )
                                      )}
                                    </Flex>
                                  </div>
                                )}
                              </div>
                            )}

                            {/* CTA */}
                            {showButtons && (
                              <div className="mt-5 pt-4 border-t border-[--gray-a5]">
                                <Text
                                  size="2"
                                  weight="bold"
                                  className="text-gray-500 text-center block"
                                >
                                  Contact for Investment
                                </Text>
                              </div>
                            )}
                          </Box>
                        </motion.div>
                      );
                    })}
                  </div>
                </motion.div>
              </AnimatePresence>
            </Tabs.Content>
          );
        })}
      </Tabs.Root>
    </div>
  );
}
